import { useEffect, useRef, useState } from 'react';

interface Props {
  nativeWidth: number;
  nativeHeight: number;
  children: React.ReactNode;
}

// Scales children down to fit narrow viewports using CSS transform.
// On wide screens (available >= nativeWidth) no scaling is applied.
export function ScaleToFit({ nativeWidth, nativeHeight, children }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(nativeWidth);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      setContainerWidth(w);
      setScale(Math.min(1, w / nativeWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nativeWidth]);

  const scaledWidth = nativeWidth * scale;
  const offsetX = (containerWidth - scaledWidth) / 2;

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: nativeHeight * scale, overflow: 'hidden', position: 'relative' }}
    >
      <div
        style={{
          width: nativeWidth,
          height: nativeHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          left: offsetX,
        }}
      >
        {children}
      </div>
    </div>
  );
}
